import React, { useEffect, useState } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import Select from "react-dropdown-select";
import { CgMathPlus } from "react-icons/cg";
import Popover from "./popover";
import axiosInstance from "../services/axiosInstance";
import { decrypt } from "../utils/cryptoUtils";
import { useLocation, useNavigate } from "react-router-dom";
import DeleteWorkflowDepartmentPopup from "../Popup/deleteWorkflowDepartmentPopup";
import { PropagateLoader } from "react-spinners";

const DepartmentForm = ({ setOrderStatusId, setFormData, setOrderId }) => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [formData, setLocalFormData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const navigate = useNavigate();

  const handlePopupOpen = (index) => {
    setSelectedRowIndex(index);
    setOpenPopup(true);
  };

  const handleRemoveRow = () => {
    removeFormFields(selectedRowIndex);
    setOpenPopup(false);
  };

  const HandleRemovePopUp = () => setOpenPopup(false);

  const truncateText = (text, maxLength = 16) => {
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  useEffect(() => {
    createOrUpdateWorkflow();
  }, []);

  const createOrUpdateWorkflow = async () => {
    const encryptedId = getQueryParams();
    if (encryptedId) {
      const decryptedId = decrypt(encryptedId);
      setOrderId(decryptedId);
      try {
        setLoading(true);
        const response = await axiosInstance.post(
          "/api/order/createOrUpdateWorkflowInit",
          {
            OrderId: parseInt(decryptedId, 10),
          }
        );

        if (response != null) {
          if (!response.hasAccess) {
            navigate("/unauthorized-access");
          }
          const departmentOptions = response.department.map((dept) => ({
            label: dept.departmentName,
            value: dept.departmentId,
          }));
          setDepartments(departmentOptions);

          const workflowData = response.data.map((workflow, index) => ({
            departmentId: workflow.departmentId,
            departmentIndex: index,
            orderWorkFlowId: workflow?.orderWorkFlowId,
            departmentName: workflow.departmentName,
            hours: workflow.hoursPerDepartment,
            dueDeliveryDate: workflow.dueDeliveryDate,
          }));
          setLocalFormData(workflowData);
          setFormData(workflowData);
          setOrderStatusId(response?.orderStatus);

          const preselected = response.data.map((workflow) => ({
            label: workflow.departmentName,
            value: workflow.departmentId,
          }));
          setSelectedDepartments(preselected);
        }
      } catch (error) {
        console.error("Error fetching workflow data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const getQueryParams = () => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("orderId");
  };

  const addFormFields = () => {
    const newIndex = formData.length;
    const newField = {
      departmentId: null,
      departmentIndex: newIndex,
      hours: "",
    };

    setLocalFormData([...formData, newField]);
    setFormData([...formData, newField]);
  };

  let removeFormFields = async (i) => {
    var newFormData = [...formData];
    const removedRow = newFormData.splice(i, 1);

    const previousDueDeliveryDate =
      i > 0 ? formData[i - 1]?.dueDeliveryDate : null;

    const affectedDepartmentIds = newFormData
      .slice(i)
      .map((row) => ({
        departmentId: row.departmentId,
        departmentIndex: row.departmentIndex,
        hours: row.hours,
      }))
      .filter((id) => id.departmentId !== null);

    newFormData = newFormData.map((row, index) => ({
      departmentId: row.departmentId,
      departmentIndex: index,
      orderWorkFlowId: row?.orderWorkFlowId,
      departmentName: row.departmentName,
      hours: row.hours,
      dueDeliveryDate: row.dueDeliveryDate,
    }));

    setLocalFormData(newFormData);
    setFormData(newFormData);
    setIsLoading(true);
    if (affectedDepartmentIds.length == 0) {
      setIsLoading(false);
      return;
    }
    // Send data to the API
    try {
      const response = await axiosInstance.post(
        "/api/order/updateAfterDelete",
        {
          PreviousDueDeliveryDate: previousDueDeliveryDate,
          AffectedDepartmentIds: affectedDepartmentIds,
        }
      );

      if (response.data && response) {
        const updatedData = response.data;
        const finalFormData = [...newFormData];

        updatedData.forEach((result) => {
          const departmentIndex =
            result.departmentIndex - 1 >= 0 ? result.departmentIndex - 1 : 0;

          if (departmentIndex !== -1) {
            finalFormData[departmentIndex].dueDeliveryDate =
              result.dueDeliveryDate;
          }
        });
        setLocalFormData(finalFormData);
        setFormData(finalFormData);
      }
    } catch (error) {
      console.error("Error updating after row deletion:", error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  const handleSelectChange = async (selectedOption, index) => {
    const departmentId = selectedOption[0]?.value || null;
    const departmentName = selectedOption[0]?.label || "";

    const updatedFormData = [...formData];

    updatedFormData[index] = {
      ...updatedFormData[index],
      departmentId,
      departmentName,
      hours:
        updatedFormData[index].hours === null ||
        updatedFormData[index].hours === ""
          ? 0
          : updatedFormData[index].hours,
      dueDeliveryDate: updatedFormData[index].hours
        ? updatedFormData[index].dueDeliveryDate // Keep existing due date if hours exist
        : index > 0
        ? updatedFormData[index - 1].dueDeliveryDate // Otherwise, take from previous row
        : null,
    };

    setLocalFormData(updatedFormData);
    setFormData(updatedFormData);

    setSelectedDepartments((prev) => [
      ...prev,
      { label: departmentName, value: departmentId },
    ]);
  };

  const handleHoursChange = async (e, index) => {
    const updatedHours = Math.max(0, parseInt(e.target.value, 10) || 0);
    const updatedFormData = [...formData];
    updatedFormData[index].hours = updatedHours;
    setLocalFormData(updatedFormData);
    setFormData(updatedFormData);
    setIsLoading(false);

    const previousDueDeliveryDate =
      index > 0 ? formData[index - 1].dueDeliveryDate : null;

    const affectedDepartmentIds = formData
      .slice(index)
      .map((row) => ({
        departmentId: row.departmentId,
        departmentIndex: row.departmentIndex,
        hours: row.hours,
      }))
      .filter((item) => item.departmentId !== null);

    try {
      const response = await axiosInstance.post(
        "/api/order/getDueDeliveryDate",
        {
          PreviousDueDeliveryDate: previousDueDeliveryDate,
          AffectedDepartmentIds: affectedDepartmentIds,
        }
      );

      if (response.data && response) {
        const updatedData = response.data;
        const newFormData = [...updatedFormData];

        updatedData.forEach((result) => {
          const departmentIndex = result.departmentIndex;

          if (departmentIndex !== -1) {
            newFormData[departmentIndex].dueDeliveryDate =
              result.dueDeliveryDate;
          }
        });
        setLocalFormData(newFormData);
        setFormData(newFormData);
      }
    } catch (error) {
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  const formatDueDeliveryDate = (dueDeliveryDate) => {
    if (!dueDeliveryDate) return "";

    const date = new Date(dueDeliveryDate);

    // Extract day, month, hours, and minutes
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-indexed
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    // Format as dd.MM | HH:mm
    return `${day}.${month} | ${hours}:${minutes}`;
  };

  return (
    <div>
      <style>
        {`
          .spinner {
            width: 24px;
            height: 24px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #3498db;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          input[type="number"] {
            -moz-appearance: textfield; /* Hides default arrows in Firefox */
          }
          input[type="number"]::-webkit-inner-spin-button,
          input[type="number"]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0; /* Removes space left by the arrows */
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div>
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <PropagateLoader color="rgb(55 110 164 / var(--tw-bg-opacity, 1))" />
          </div>
        ) : (
          <div className="2xl:w-3/4 xl:w-5/6 lg:w-4/5 w-full 2xl:px-0 px-5 mx-auto z-10 relative">
            <div className="xl:overflow-x-visible overflow-x-scroll  xl:pr-0 pr-1">
              <form className=" xl:pr-0 pr-1">
                {formData.map((work, index) => (
                  <div
                    key={index}
                    className="relative mb-5 font-inter flex justify-between xl:gap-7 gap-5 items-center "
                  >
                    <Select
                      className="3xl:min-w-[250px] min-w-[200px] font-interRegular appearance-none !border h-12 xl:h-10 3xl:h-12 !border-gray !leading-relaxed !text-2xl xl:!text-sm 3xl:!text-2xl bg-white !rounded-lg !w-full !py-3 !px-5 xl:!py-2 xl:!px-3 3xl:!py-3 3xl:!px-5 text-gray-600 placeholder:!text-2xl xl:placeholder:!text-sm 3xl:placeholder:!text-2xl placeholder:!text-gray-400 hover:!border-blue-600 focus:!border-blue-600 focus:!outline-none !shadow-none truncate-label"
                      placeholder="Abteilung auswählen"
                      options={departments}
                      getOptionLabel={(option) => option.label} // Full text in dropdown
                      getValue={(selected) =>
                        selected.map((opt) => ({
                          ...opt,
                          label: truncateText(opt.label), // Trimmed text in selected value
                        }))
                      }
                      values={departments
                        .filter((dept) => dept.value === work.departmentId)
                        .map((dept) => ({
                          ...dept,
                          label: truncateText(dept.label), // Also truncate selected value
                        }))}
                      onChange={(selectedOption) =>
                        handleSelectChange(selectedOption, index)
                      }
                      disabled={isLoading}
                      onMenuOpen={() => setIsOpen(true)}
                      onMenuClose={() => setIsOpen(false)}
                      menuPortalTarget={document.body} // Render dropdown outside the parent container
                      styles={{
                        menu: (base) => ({
                          ...base,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "auto",
                          minWidth: "200px",
                          maxWidth: "300px",
                          zIndex: 9999,
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999, // Ensures dropdown appears above other elements
                        }),
                      }}
                    />
                    <div className="3xl:min-w-[360px] xl:min-w-[300px] ">
                      <label className="appearance-none text-center text-2xl xl:text-sm 3xl:text-2xl border whitespace-nowrap font-interRegular h-12 xl:h-10 3xl:h-12 bg-white border-gray rounded-lg w-full py-3 2xl:px-11 px-5 text-gray-600 inline-block">
                        <span className="text-gray-500">
                          {formatDueDeliveryDate(work.dueDeliveryDate)}
                        </span>{" "}
                      </label>
                    </div>
                    <div className="relative flex items-center">
                      {/* Number Input with Hidden Default Spinner */}
                      <input
                        name="Zeit"
                        type="number"
                        value={work.hours}
                        readOnly
                        inputMode="none"
                        onKeyDown={(e) => {
                          if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
                            e.preventDefault();
                          }
                        }}
                        min={0}
                        disabled={!work.departmentId || isLoading}
                        className="3xl:w-24 w-20 h-12 xl:h-10 3xl:h-12 text-center font-interRegular border border-gray rounded-lg pr-7 text-gray-600 placeholder:text-gray-400 focus:border-blue-600 focus:outline-none focus:shadow-outline appearance-none"
                        style={{
                          MozAppearance: "textfield", // Hides default arrows in Firefox
                        }}
                      />

                      {/* Custom Increment Button */}
                      <button
                        type="button"
                        disabled={!work.departmentId || isLoading}
                        onClick={() =>
                          handleHoursChange(
                            { target: { value: work.hours + 1 } },
                            index
                          )
                        }
                        className={`absolute right-2 top-[30%] transform -translate-y-1/2 text-gray-600 bg-gray-200 px-1 py-[1px] text-xs rounded cursor-pointer ${
                          !work.departmentId
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-gray-300"
                        }`}
                      >
                        ▲
                      </button>

                      {/* Custom Decrement Button */}
                      <button
                        type="button"
                        disabled={
                          work.hours <= 0 || !work.departmentId || isLoading
                        }
                        onClick={() =>
                          handleHoursChange(
                            { target: { value: Math.max(0, work.hours - 1) } },
                            index
                          )
                        }
                        className={`absolute right-2 bottom-[30%] transform translate-y-1/2 text-gray-600 bg-gray-200 px-1 py-[1px] text-xs rounded cursor-pointer ${
                          work.hours <= 0
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-gray-300"
                        }`}
                      >
                        ▼
                      </button>
                    </div>

                    {/* Add the loader (spinner) here */}
                    {isLoading && <div className="spinner" />}

                    <div>
                      <Popover
                        trigger="hover"
                        content={
                          <p
                            className="absolute 3xl:-right-10 xl:-right-7 -right-10 -top-9 3xl:-top-9 xl:-top-8 text-blue-400 px-2 py-1 text-lg xl:text-xs 3xl:text-lg bg-red rounded w-auto whitespace-nowrap after:content-[' '] after:-bottom-2 after:left-0 after:-right-3 after:absolute after:border after:border-l-8 after:border-r-8 after:border-transparent after:w-0 after:h-0 after:m-auto after:border-t-[10px] after:border-t-red shadow-[2px_2px_4px_1px_rgba(0,0,0,0.2)] font-interRegular max-w-fit"
                            style={{}}
                          >
                            Abteilung löschen
                          </p>
                        }
                      >
                        <button
                          type="button"
                          className="rounded-lg p-3 w-12 xl:w-10 3xl:w-12 text-center flex items-center justify-center h-12 xl:h-10 3xl:h-12 border border-transparent shadow-[0_0_4px_rgba(253,37,30,0.3)] hover:border-[rgba(238,18,10,0.5)] transition-all ease-in focus:border-[rgba(238,18,10,0.5)]"
                          style={{
                            backgroundColor: "rgba(238, 18, 10, 0.05)",
                            borderRadius: "5px",
                            cursor: "pointer",
                          }}
                          // onClick={() => removeFormFields(index)}
                          onClick={() => handlePopupOpen(index)}
                        >
                          <RiDeleteBin6Line className="text-[#EE120A]" />
                        </button>
                      </Popover>
                    </div>
                  </div>
                ))}
              </form>
            </div>
            <button
              className="button add flex items-center justify-center font-inter gap-4 text-blue underline  text-2xl xl:text-lg 3xl:text-2xl"
              type="button"
              onClick={() => addFormFields()}
            >
              <span className="p-3 w-12 xl:w-10 3xl:w-12 text-center flex items-center justify-center h-12 xl:h-10 3xl:h-12 border bg-blue-300 border-transparent poiner rounded-md">
                <CgMathPlus />
              </span>
              Abteilung hinzufügen
            </button>
            <div className="bg-blue-300 rounded-xl w-full p-3  text-3xl xl:text-2xl 3xl:text-4xl  font-bold font-robotoBold text-center text-blue mt-8">
              Fertigstellung bis:{" "}
              <span>
                {formData.length > 0
                  ? formatDueDeliveryDate(
                      formData[formData.length - 1].dueDeliveryDate
                    )
                  : ""}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* modal popup */}
      <DeleteWorkflowDepartmentPopup
        openPopUp={openPopup}
        closePopUp={HandleRemovePopUp}
        removeRow={handleRemoveRow}
      />
    </div>
  );
};

export default DepartmentForm;
